import { useRef } from 'react'
import { Box, TableCell, TableSortLabel, Tooltip, useTheme } from '@mui/material'
import { C33Column } from './columns'
import { MAX_COL_W, MIN_COL_W } from './use-table-prefs'
import { c100Tokens } from './c100-tokens'
import { SortDir, SortKey } from '../../pages/carnivore100/carnivore100.util'

const MONO = "'IBM Plex Mono', monospace"

export default function HeaderCell({
  col,
  width,
  pinLeft,
  active,
  sortDir,
  onSort,
  onResize,
  onResetWidth,
  onDragStart,
  onDropOn,
  dropSide
}: {
  col: C33Column
  width: number
  pinLeft?: number
  active: boolean
  sortDir: SortDir
  onSort: (key: SortKey) => void
  onResize: (id: string, width: number) => void
  onResetWidth: (id: string) => void
  onDragStart: (id: string) => void
  onDropOn: (id: string) => void
  dropSide: 'left' | null
}) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  const drag = useRef<{ startX: number; startW: number } | null>(null)

  const labelSx = {
    fontFamily: MONO,
    fontSize: 10.5,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: active ? `${t.accent} !important` : `${t.muted} !important`,
    '& .MuiTableSortLabel-icon': {
      color: `${t.accent} !important`,
      opacity: active ? 1 : 0
    },
    '&:hover': { color: `${t.text} !important` }
  }

  const inner =
    col.key === null ? (
      <Box component="span" sx={labelSx}>
        {col.label}
      </Box>
    ) : (
      <TableSortLabel
        active={active}
        direction={active ? sortDir : 'asc'}
        onClick={() => onSort(col.key as SortKey)}
        sx={labelSx}
      >
        {col.label}
      </TableSortLabel>
    )

  return (
    <TableCell
      draggable={!col.locked}
      onDragStart={e => {
        if (col.locked) return
        // some browsers refuse to start a drag without payload
        e.dataTransfer.setData('text/plain', col.id)
        e.dataTransfer.effectAllowed = 'move'
        onDragStart(col.id)
      }}
      onDragOver={e => {
        if (col.locked) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        onDropOn(col.id)
      }}
      onDrop={e => {
        e.preventDefault()
      }}
      sortDirection={active ? sortDir : false}
      sx={{
        py: 1.25,
        whiteSpace: 'nowrap',
        cursor: col.locked ? 'default' : 'grab',
        // `&&` outranks the head row's `& th` rule, which would otherwise win on
        // specificity and let the scrolling columns paint over the pinned ones
        '&&': {
          boxSizing: 'border-box',
          width,
          minWidth: width,
          maxWidth: width,
          ...(pinLeft !== undefined
            ? { position: 'sticky', left: pinLeft, zIndex: 30, bgcolor: t.card }
            : { position: 'relative' }),
          ...(dropSide
            ? { boxShadow: `inset 2px 0 0 ${t.accent}` }
            : {})
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {col.help ? (
          <Tooltip title={col.help} arrow enterDelay={200}>
            <Box component="span" sx={{ display: 'inline-flex', minWidth: 0 }}>
              {inner}
            </Box>
          </Tooltip>
        ) : (
          inner
        )}
      </Box>

      {/* Resize grip. Pointer capture keeps the drag alive even when the cursor
          outruns the 6px strip; double-click restores the column's default. */}
      <Box
        onPointerDown={e => {
          e.stopPropagation()
          e.preventDefault()
          drag.current = { startX: e.clientX, startW: width }
          ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
        }}
        onPointerMove={e => {
          if (!drag.current) return
          const next = drag.current.startW + (e.clientX - drag.current.startX)
          onResize(col.id, Math.min(MAX_COL_W, Math.max(MIN_COL_W, next)))
        }}
        onPointerUp={e => {
          drag.current = null
          ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
        }}
        onClick={e => e.stopPropagation()}
        onDoubleClick={e => {
          e.stopPropagation()
          onResetWidth(col.id)
        }}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: 7,
          cursor: 'col-resize',
          touchAction: 'none',
          zIndex: 2,
          '&:hover::after, &:active::after': {
            content: '""',
            position: 'absolute',
            top: 6,
            bottom: 6,
            right: 3,
            width: 2,
            borderRadius: 2,
            bgcolor: t.accent
          }
        }}
      />
    </TableCell>
  )
}
